import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";
import type { ReactElement } from "react";
import type { NotificationStreamItem } from "@situate/contracts";

import { PAGE_HEADER_HEIGHT } from "@/shared/ui/page-header";
import { NotificationPopupCard } from "@/features/deployment-dashboard/notification-center/notification-popup-card";

/** How long a popup stays visible once it occupies a slot, in milliseconds. */
export const NOTIFICATION_POPUP_VISIBLE_MS = 10_000;
/** Maximum number of popups shown concurrently; additional arrivals queue. */
export const MAX_VISIBLE_NOTIFICATION_POPUPS = 3;

export interface NotificationPopupQueueItem {
  /** Unique per popup occurrence (not just per notification, since the same
   * notification could theoretically be re-added after removal). */
  popupId: string;
  item: NotificationStreamItem;
}

function NoopSnackbarTransition({ children }: { children: ReactElement }) {
  return children;
}

interface NotificationPopupStackProps {
  /** Full pending+visible queue, oldest first. The first
   * `MAX_VISIBLE_NOTIFICATION_POPUPS` entries are rendered; the rest wait. */
  queue: readonly NotificationPopupQueueItem[];

  /** Called when a popup's visible timer elapses; the caller should remove it from `queue`. */
  onExpire: (popupId: string) => void;
  /** Called when a popup is clicked; the caller should remove it from `queue` and open the bell panel. */
  onSelect: (popupId: string) => void;
}

/**
 * Fixed, top-right stack of notification popups. Shows at most
 * `MAX_VISIBLE_NOTIFICATION_POPUPS` concurrently; further queued items wait
 * until a visible slot frees up. MUI's `Snackbar` starts the
 * `NOTIFICATION_POPUP_VISIBLE_MS` timer only once an item occupies a visible
 * slot, so promoted items always get a full visible interval.
 */
export function NotificationPopupStack({ queue, onExpire, onSelect }: NotificationPopupStackProps) {
  const visibleItems = queue.slice(0, MAX_VISIBLE_NOTIFICATION_POPUPS);

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: PAGE_HEADER_HEIGHT + 12,
        right: 16,
        zIndex: (theme) => theme.zIndex.snackbar,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        pointerEvents: "none",
      }}
    >
      {visibleItems.map((entry) => (
        <Box key={entry.popupId} sx={{ pointerEvents: "auto" }}>
          <Snackbar
            open
            autoHideDuration={NOTIFICATION_POPUP_VISIBLE_MS}
            slots={{ transition: NoopSnackbarTransition }}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            onClose={(_event, reason) => {
              if (reason === "timeout") {
                onExpire(entry.popupId);
              }
            }}
            sx={{ position: "static", transform: "none" }}
          >
            <NotificationPopupCard
              item={entry.item}
              onClick={() => onSelect(entry.popupId)}
              onDismiss={() => onExpire(entry.popupId)}
              durationMs={NOTIFICATION_POPUP_VISIBLE_MS}
            />
          </Snackbar>
        </Box>
      ))}
    </Box>
  );
}
